import React, { Fragment, useState, useEffect } from 'react';
import { Card, Col, Row, Breadcrumb, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import './estadisticas-consumo.scss';
import { useAuth } from '../../hooks/useAuth';
import { estadisticasConsumoService } from '../../services/estadisticasConsumoService';
// Gráfico de líneas (ApexCharts)
import ReactApexChart from 'react-apexcharts';

const EstadisticasConsumo = () => {
    // Hook de autenticación
    const { user, asesorId, isAuthenticated } = useAuth();
    
    const [showFilters, setShowFilters] = useState(true);
    
    // Función para calcular el rango de 12 meses
    const calcularRango12Meses = () => {
        const hoy = new Date();
        const mesActual = hoy.getMonth() + 1; // 1-12
        const añoActual = hoy.getFullYear();
        
        // Calcular 12 meses atrás
        let mesDesde = mesActual - 11; // 12 meses incluyendo el actual
        let añoDesde = añoActual;
        
        // Si el mes es negativo, ajustar al año anterior
        if (mesDesde <= 0) {
            mesDesde += 12;
            añoDesde -= 1;
        }
        
        return {
            mesDesde,
            añoDesde,
            mesHasta: mesActual,
            añoHasta: añoActual
        };
    };

    // Estados para el filtro de rango de meses (inicializado con 12 meses)
    const rangoInicial = calcularRango12Meses();
    const [mesDesde, setMesDesde] = useState(rangoInicial.mesDesde);
    const [añoDesde, setAñoDesde] = useState(rangoInicial.añoDesde);
    const [mesHasta, setMesHasta] = useState(rangoInicial.mesHasta);
    const [añoHasta, setAñoHasta] = useState(rangoInicial.añoHasta);
    
    // Estado para el límite (se inicializará desde el servidor)
    const [limite, setLimite] = useState(300); // Valor temporal hasta cargar desde servidor
    
    // Estado para las conversaciones contratadas
    const [conversacionesContratadas, setConversacionesContratadas] = useState(0);
    
    // Estado para el plan compartido
    const [planCompartido, setPlanCompartido] = useState(false);
    
    // Estados para las estadísticas de consumo
    const [estadisticasConsumo, setEstadisticasConsumo] = useState({
        series: [
            {
                name: 'Conversaciones',
                data: []
            }
        ],
        options: {
            chart: {
                type: 'bar',
                height: 280,
                toolbar: {
                    show: true
                }
            },
            colors: ['#6a8bff'],
            xaxis: {
                categories: [],
                labels: {
                    style: {
                        fontSize: '12px'
                    },
                    rotate: -45
                }
            },
            yaxis: {
                title: {
                    text: 'Cantidad de Conversaciones'
                },
                min: 0,
                max: 300, // Se actualizará dinámicamente con el límite del servidor
                forceNiceScale: false,
                tickAmount: 10
            },
            title: {
                text: 'Estadísticas de Conversaciones por Mes',
                align: 'left',
                style: {
                    fontSize: '16px',
                    fontWeight: 'bold'
                }
            },
            grid: {
                borderColor: '#f1f1f1'
            },
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: '11px',
                    fontWeight: 'bold'
                }
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    columnWidth: '60%',
                    dataLabels: {
                        position: 'top'
                    }
                }
            },
            tooltip: {
                y: {
                    formatter: function (val) {
                        return val + " conversaciones"
                    }
                }
            },
            annotations: {
                yaxis: [
                    {
                        y: 300, // Se actualizará dinámicamente con el límite del servidor
                        borderColor: '#dc3545',
                        borderWidth: 3,
                        strokeDashArray: 0,
                        label: {
                            text: 'Límite: 300', // Se actualizará dinámicamente
                            style: {
                                color: '#dc3545',
                                background: '#fff',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                border: '1px solid #dc3545',
                                borderRadius: '4px',
                                padding: '2px 6px'
                            },
                            position: 'right',
                            offsetY: -10
                        }
                    }
                ]
            }
        }
    });

    // Estado de carga
    const [cargando, setCargando] = useState(false);
    const [guardandoLimite, setGuardandoLimite] = useState(false);
    const [mensajeLimite, setMensajeLimite] = useState('');

    // Función para actualizar el gráfico cuando cambie el límite
    const actualizarGraficoConLimite = (nuevoLimite) => {
        const datosActuales = estadisticasConsumo.series[0]?.data || [];
        const maxDatos = Math.max(...datosActuales, 0);
        const maxY = Math.max(maxDatos, nuevoLimite) + (nuevoLimite * 0.1); // 10% de margen sobre el límite
        
        setEstadisticasConsumo(prev => ({
            ...prev,
            options: {
                ...prev.options,
                yaxis: {
                    ...prev.options.yaxis,
                    min: 0,
                    max: maxY,
                    forceNiceScale: false,
                    tickAmount: 10
                },
                annotations: {
                    yaxis: [
                        {
                            y: nuevoLimite,
                            borderColor: '#dc3545',
                            borderWidth: 3,
                            strokeDashArray: 0,
                            opacity: 1,
                            label: {
                                text: `Límite: ${nuevoLimite}`,
                                style: {
                                    color: '#dc3545',
                                    background: '#fff',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    border: '1px solid #dc3545',
                                    borderRadius: '4px',
                                    padding: '2px 6px'
                                },
                                position: 'right',
                                offsetY: -10
                            }
                        }
                    ]
                }
            }
        }));
    };

    // Función para guardar el límite en el servidor
    const guardarLimite = async () => {
        try {
            setGuardandoLimite(true);
            setMensajeLimite('');
            
            // Validar que el límite sea un número válido y positivo
            if (isNaN(limite) || limite <= 0) {
                setMensajeLimite('❌ Error: El límite debe ser un número positivo');
                return;
            }
            
            console.log(`Guardando límite: ${limite}...`);
            
            await estadisticasConsumoService.saveLimit(null, limite);
            
            setMensajeLimite('✅ Límite guardado exitosamente');
            
            // Limpiar el mensaje después de 3 segundos
            setTimeout(() => {
                setMensajeLimite('');
            }, 3000);
            
        } catch (error) {
            console.error('Error al guardar el límite:', error);
            setMensajeLimite(`❌ Error: ${error.message}`);
            
            // Limpiar el mensaje de error después de 5 segundos
            setTimeout(() => {
                setMensajeLimite('');
            }, 5000);
        } finally {
            setGuardandoLimite(false);
        }
    };

    // Función para obtener estadísticas de consumo
    const obtenerEstadisticasConsumo = async () => {
        try {
            setCargando(true);
            console.log(`Obteniendo estadísticas de consumo desde ${mesDesde}/${añoDesde} hasta ${mesHasta}/${añoHasta}...`);
            
            const response = await estadisticasConsumoService.getEstadisticasConsumo(
                null, // token (se obtiene del localStorage)
                mesDesde,
                añoDesde,
                mesHasta,
                añoHasta
            );
            
            // Actualizar el límite con el valor del servidor
            if (response.limite && response.limite !== limite) {
                setLimite(response.limite);
                console.log(`🔢 [Estadísticas Consumo] Límite actualizado desde servidor: ${response.limite}`);
            }
            
            // Actualizar las conversaciones contratadas con el valor del servidor
            if (response.conversacionesContratadas !== undefined) {
                setConversacionesContratadas(response.conversacionesContratadas);
                console.log(`📊 [Estadísticas Consumo] Conversaciones contratadas actualizadas desde servidor: ${response.conversacionesContratadas}`);
            }
            
            // Actualizar el plan compartido con el valor del servidor
            if (response.planCompartido !== undefined) {
                setPlanCompartido(response.planCompartido);
                console.log(`📊 [Estadísticas Consumo] Plan compartido actualizado desde servidor: ${response.planCompartido}`);
            }
            
            // Procesar los datos para el gráfico
            const labels = response.serie.map(item => item.label);
            const valores = response.serie.map(item => item.valor);
            
            // Usar el límite del servidor para el gráfico
            const limiteActual = response.limite || limite;
            
            // Calcular el máximo valor de los datos
            const maxDatos = Math.max(...valores, 0);
            
            // Calcular el rango del eje Y: máximo entre datos y límite + un pequeño margen
            const maxY = Math.max(maxDatos, limiteActual) + (limiteActual * 0.1); // 10% de margen sobre el límite
            
            setEstadisticasConsumo(prev => ({
                ...prev,
                series: [
                    {
                        name: 'Conversaciones',
                        data: valores
                    }
                ],
                options: {
                    ...prev.options,
                    xaxis: {
                        ...prev.options.xaxis,
                        categories: labels
                    },
                    yaxis: {
                        ...prev.options.yaxis,
                        min: 0,
                        max: maxY,
                        forceNiceScale: false,
                        tickAmount: 10
                    },
                    annotations: {
                        yaxis: [
                        {
                            y: limiteActual,
                            borderColor: '#dc3545',
                            borderWidth: 3,
                            strokeDashArray: 0,
                            opacity: 1,
                            label: {
                                    text: `Límite: ${limiteActual}`,
                                    style: {
                                        color: '#dc3545',
                                        background: '#fff',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        border: '1px solid #dc3545',
                                        borderRadius: '4px',
                                        padding: '2px 6px'
                                    },
                                    position: 'right',
                                    offsetY: -10
                                }
                            }
                        ]
                    }
                }
            }));
            
        } catch (error) {
            console.error('Error al obtener estadísticas de consumo:', error);
            // En caso de error, mostrar datos vacíos
            // En caso de error, mostrar datos vacíos pero mantener el límite visible
            const maxY = limite;
            
            setEstadisticasConsumo(prev => ({
                ...prev,
                series: [
                    {
                        name: 'Conversaciones',
                        data: []
                    }
                ],
                options: {
                    ...prev.options,
                    xaxis: {
                        ...prev.options.xaxis,
                        categories: []
                    },
                    yaxis: {
                        ...prev.options.yaxis,
                        min: 0,
                        max: maxY,
                        forceNiceScale: false,
                        tickAmount: 10
                    },
                    annotations: {
                        yaxis: [
                        {
                            y: limiteActual,
                            borderColor: '#dc3545',
                            borderWidth: 3,
                            strokeDashArray: 0,
                            opacity: 1,
                            label: {
                                    text: `Límite: ${limiteActual}`,
                                    style: {
                                        color: '#dc3545',
                                        background: '#fff',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        border: '1px solid #dc3545',
                                        borderRadius: '4px',
                                        padding: '2px 6px'
                                    },
                                    position: 'right',
                                    offsetY: -10
                                }
                            }
                        ]
                    }
                }
            }));
        } finally {
            setCargando(false);
        }
    };


    // Efecto para cargar datos solo al montar el componente
    useEffect(() => {
        if (isAuthenticated) {
            obtenerEstadisticasConsumo();
        }
    }, [isAuthenticated]);

    return (
        <Fragment>
            <div className="main-content-wrapper">
                <div className="breadcrumb">
                    <span>Dashboard</span> <span className="chevron">›</span>{' '}
                    <span className="current">Estadísticas Consumo</span>
                </div>
                
                <div className="main-card">
                    <div className="header-row">
                        <h5 className="title">Estadísticas de Consumo</h5>
                        <Button
                            variant="outline-primary"
                            onClick={() => setShowFilters(!showFilters)}
                            className="filter-btn"
                            style={{ 
                                borderColor: '#6a8bff',
                                color: '#6a8bff'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#6a8bff';
                                e.target.style.borderColor = '#6a8bff';
                                e.target.style.color = '#fff';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.borderColor = '#6a8bff';
                                e.target.style.color = '#6a8bff';
                            }}
                        >
                            <FontAwesomeIcon icon={faFilter} className="me-2" />
                            Filtros
                        </Button>
                    </div>
                    
                    <hr className="divider" />
                    
                    {/* Panel de filtros */}
                    {showFilters && (
                        <div className="filters-panel">
                            <Row>
                                <Col md={3}>
                                    <div className="form-group">
                                        <label>Mes desde:</label>
                                        <select
                                            className="form-control"
                                            value={mesDesde}
                                            onChange={(e) => setMesDesde(parseInt(e.target.value))}
                                        >
                                            <option value={1}>Enero</option>
                                            <option value={2}>Febrero</option>
                                            <option value={3}>Marzo</option>
                                            <option value={4}>Abril</option>
                                            <option value={5}>Mayo</option>
                                            <option value={6}>Junio</option>
                                            <option value={7}>Julio</option>
                                            <option value={8}>Agosto</option>
                                            <option value={9}>Septiembre</option>
                                            <option value={10}>Octubre</option>
                                            <option value={11}>Noviembre</option>
                                            <option value={12}>Diciembre</option>
                                        </select>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="form-group">
                                        <label>Año desde:</label>
                                        <select
                                            className="form-control"
                                            value={añoDesde}
                                            onChange={(e) => setAñoDesde(parseInt(e.target.value))}
                                        >
                                            {Array.from({ length: 5 }, (_, i) => {
                                                const year = new Date().getFullYear() - i;
                                                return (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="form-group">
                                        <label>Mes hasta:</label>
                                        <select
                                            className="form-control"
                                            value={mesHasta}
                                            onChange={(e) => setMesHasta(parseInt(e.target.value))}
                                        >
                                            <option value={1}>Enero</option>
                                            <option value={2}>Febrero</option>
                                            <option value={3}>Marzo</option>
                                            <option value={4}>Abril</option>
                                            <option value={5}>Mayo</option>
                                            <option value={6}>Junio</option>
                                            <option value={7}>Julio</option>
                                            <option value={8}>Agosto</option>
                                            <option value={9}>Septiembre</option>
                                            <option value={10}>Octubre</option>
                                            <option value={11}>Noviembre</option>
                                            <option value={12}>Diciembre</option>
                                        </select>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="form-group">
                                        <label>Año hasta:</label>
                                        <select
                                            className="form-control"
                                            value={añoHasta}
                                            onChange={(e) => setAñoHasta(parseInt(e.target.value))}
                                        >
                                            {Array.from({ length: 5 }, (_, i) => {
                                                const year = new Date().getFullYear() - i;
                                                return (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </Col>
                                <Col md={2} className="d-flex align-items-end">
                                    <Button
                                        variant="primary"
                                        onClick={obtenerEstadisticasConsumo}
                                        className="w-100"
                                        style={{ backgroundColor: '#6a8bff', borderColor: '#6a8bff' }}
                                    >
                                        Aplicar
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    )}
                    
                    {/* Gráfico de consumo */}
                    <div className="chart-container">
                        <Card>
                            <Card.Body>
                                {/* Controles de límite */}
                                <div className="d-flex justify-content-end mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <label className="mb-0 fw-bold me-2" style={{ fontSize: '13px', lineHeight: '32px' }}>Contratado:</label>
                                        <span className="me-3" style={{ 
                                            fontSize: '12px', 
                                            padding: '6px 10px',
                                            backgroundColor: 'transparent',
                                            color: '#000000',
                                            fontWeight: '700'
                                        }}>
                                            {conversacionesContratadas.toLocaleString()}
                                        </span>
                                        {planCompartido && (
                                            <span className="me-3" style={{ 
                                                fontSize: '12px', 
                                                padding: '6px 10px',
                                                backgroundColor: 'transparent',
                                                color: '#6a8bff',
                                                fontWeight: '700'
                                            }}>
                                                Plan compartido
                                            </span>
                                        )}
                                        <label className="mb-0 fw-bold me-2" style={{ fontSize: '13px', lineHeight: '32px' }}>Límite:</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            style={{ 
                                                width: '100px',
                                                height: '32px',
                                                fontSize: '13px'
                                            }}
                                            value={limite}
                                            onChange={(e) => {
                                                const valor = e.target.value;
                                                if (valor === '') {
                                                    setLimite(0);
                                                    return;
                                                }
                                                const nuevoLimite = parseInt(valor);
                                                if (!isNaN(nuevoLimite) && nuevoLimite >= 0) {
                                                    setLimite(nuevoLimite);
                                                    actualizarGraficoConLimite(nuevoLimite);
                                                }
                                            }}
                                            min="0"
                                            placeholder="300"
                                        />
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            style={{ 
                                                height: '32px', 
                                                fontSize: '13px',
                                                backgroundColor: '#6a8bff',
                                                borderColor: '#6a8bff'
                                            }}
                                            onClick={guardarLimite}
                                            disabled={guardandoLimite}
                                            title="Guardar límite"
                                        >
                                            {guardandoLimite ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                    Guardando...
                                                </>
                                            ) : (
                                                'Guardar'
                                            )}
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Mensaje de estado del límite */}
                                {mensajeLimite && (
                                    <div className={`alert ${mensajeLimite.includes('✅') ? 'alert-success' : 'alert-danger'} alert-dismissible fade show mt-2`} role="alert">
                                        {mensajeLimite}
                                        <button 
                                            type="button" 
                                            className="btn-close" 
                                            onClick={() => setMensajeLimite('')}
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                )}
                                
                                {cargando ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Cargando...</span>
                                        </div>
                                        <p className="mt-3 text-muted">Cargando estadísticas de consumo...</p>
                                    </div>
                                ) : (
                                    <ReactApexChart
                                        options={estadisticasConsumo.options}
                                        series={estadisticasConsumo.series}
                                        type="bar"
                                        height={280}
                                    />
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default EstadisticasConsumo;
